'use client';

import { Box, Grid, HStack, VStack } from '@terra/ui-styled-system';
import { Button, Text, TextDiffView } from '@terra/ui-design-system';
import { useCredentialEditStore } from '../../store/credentialEditStore';
import { useCredentialUpdateQueueStore } from '../../store/credentialUpdateQueueStore';
import type { PendingCredentialFieldEdit } from '../../store/credentialEditStore';
import type { CredentialEditableField } from '../../utils/chatEditableObjectUtils';

const CREDENTIAL_FIELD_LABELS: Record<CredentialEditableField, string> = {
  role: 'Role',
  roleId: 'Custom role',
  roleDescription: 'Role description',
};

type CredentialFieldPendingEditProps = {
  pendingEdit: PendingCredentialFieldEdit;
  onAccept: () => void;
  onDiscard: () => void;
};

function CredentialFieldPendingEdit({
  pendingEdit,
  onAccept,
  onDiscard,
}: CredentialFieldPendingEditProps) {
  const fieldLabel = CREDENTIAL_FIELD_LABELS[pendingEdit.field];
  const beforeDisplay = pendingEdit.before ?? '—';
  const afterDisplay = pendingEdit.after ?? '—';

  return (
    <VStack
      gap={3}
      alignItems="stretch"
      p={4}
      borderRadius="lg"
      borderWidth="1px"
      borderColor="border.info"
      bg="bg.canvas"
    >
      <HStack justifyContent="space-between" alignItems="center" gap={2}>
        <Text fontSize="sm" fontWeight="semibold" color="fg.default">
          {fieldLabel}
        </Text>
        <HStack gap={1}>
          <Button size="xs" variant="ghost" onClick={onDiscard} type="button">
            Discard
          </Button>
          <Button size="xs" variant="solid" onClick={onAccept} type="button">
            Accept
          </Button>
        </HStack>
      </HStack>
      <Grid columns={2} gap={4}>
        <VStack gap={1} alignItems="stretch">
          <Text fontSize="xs" color="fg.muted">
            Before
          </Text>
          <Box
            p={3}
            borderRadius="md"
            borderWidth="1px"
            borderColor="border.subtle"
            bg="bg.subtle"
            minH="80px"
          >
            <Text fontSize="sm" whiteSpace="pre-wrap">
              {beforeDisplay}
            </Text>
          </Box>
        </VStack>
        <VStack gap={1} alignItems="stretch">
          <Text fontSize="xs" color="fg.muted">
            After
          </Text>
          <Box
            p={3}
            borderRadius="md"
            borderWidth="1px"
            borderColor="border.info"
            bg="bg.canvas"
            minH="80px"
          >
            <TextDiffView before={beforeDisplay} after={afterDisplay} />
          </Box>
        </VStack>
      </Grid>
    </VStack>
  );
}

type CredentialCopilotPendingEditsSectionProps = {
  credentialId: string;
  credentialLabel: string;
  baseFields: {
    role: string;
    roleId: string | null;
    roleDescription: string | null;
  };
  onAccepted: (nextFields: {
    role: string;
    roleId: string | null;
    roleDescription: string | null;
  }) => void;
};

export function CredentialCopilotPendingEditsSection({
  credentialId,
  credentialLabel,
  baseFields,
  onAccepted,
}: CredentialCopilotPendingEditsSectionProps) {
  const pendingAiEdits = useCredentialEditStore(
    (state) => state.pendingAiEdits[credentialId],
  );
  const acceptAiEdits = useCredentialEditStore((state) => state.acceptAiEdits);
  const discardAiEdits = useCredentialEditStore(
    (state) => state.discardAiEdits,
  );

  if (!pendingAiEdits?.fields.length) {
    return null;
  }

  const handleAcceptAll = () => {
    const nextFields = acceptAiEdits(credentialId, undefined, {
      baseDraftFields: baseFields,
    });
    if (nextFields) {
      onAccepted(nextFields);
    }
  };

  const handleDiscardAll = () => {
    const pendingBeforeDiscard = pendingAiEdits;
    discardAiEdits(credentialId);
    const consumedSourceUpdateIds = [
      ...new Set(
        (pendingBeforeDiscard?.fields ?? [])
          .map((entry) => entry.sourceUpdateId)
          .filter(
            (id): id is string => typeof id === 'string' && id.length > 0,
          ),
      ),
    ];
    if (consumedSourceUpdateIds.length > 0) {
      useCredentialUpdateQueueStore
        .getState()
        .removeCredentialUpdatesByIds(consumedSourceUpdateIds);
    }
  };

  const handleAcceptField = (field: CredentialEditableField) => {
    const nextFields = acceptAiEdits(credentialId, [field], {
      baseDraftFields: baseFields,
    });
    if (nextFields) {
      onAccepted(nextFields);
    }
  };

  const handleDiscardField = (field: CredentialEditableField) => {
    const pendingBeforeDiscard = pendingAiEdits;
    discardAiEdits(credentialId, [field]);
    const remainingFields =
      useCredentialEditStore.getState().getPendingAiEdits(credentialId)
        ?.fields ?? [];
    if (remainingFields.length > 0) {
      return;
    }
    const consumedSourceUpdateIds = [
      ...new Set(
        (pendingBeforeDiscard?.fields ?? [])
          .map((entry) => entry.sourceUpdateId)
          .filter(
            (id): id is string => typeof id === 'string' && id.length > 0,
          ),
      ),
    ];
    if (consumedSourceUpdateIds.length > 0) {
      useCredentialUpdateQueueStore
        .getState()
        .removeCredentialUpdatesByIds(consumedSourceUpdateIds);
    }
  };

  return (
    <Box
      p={3}
      borderRadius="md"
      borderWidth="1px"
      borderColor="border.info"
      bg="bg.subtle"
    >
      <VStack alignItems="stretch" gap={3}>
        <HStack justifyContent="space-between" alignItems="center" gap={2}>
          <VStack gap={1} alignItems="flex-start">
            <Text fontSize="sm" fontWeight="semibold">
              {pendingAiEdits.fields.length} AI-proposed change
              {pendingAiEdits.fields.length === 1 ? '' : 's'} for{' '}
              {credentialLabel}
            </Text>
            {pendingAiEdits.reason ? (
              <Text fontSize="xs" color="fg.muted" fontStyle="italic">
                {pendingAiEdits.reason}
              </Text>
            ) : null}
          </VStack>
          <HStack gap={2}>
            <Button size="xs" variant="outline" onClick={handleDiscardAll}>
              Discard all
            </Button>
            <Button size="xs" variant="solid" onClick={handleAcceptAll}>
              Accept all
            </Button>
          </HStack>
        </HStack>
        {pendingAiEdits.fields.map((entry) => (
          <CredentialFieldPendingEdit
            key={entry.field}
            pendingEdit={entry}
            onAccept={() => handleAcceptField(entry.field)}
            onDiscard={() => handleDiscardField(entry.field)}
          />
        ))}
      </VStack>
    </Box>
  );
}
